import { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PACKAGE_TYPE_LABELS } from './types';
import { useServicePackages } from './ServicePackageContext';
import { ServicePackageCard } from './components/ServicePackageCard';
import { Input } from '@/components/ui/input';
import { Routes } from '@/utils/routes';
import i18n from '@/utils/i18n';

export default function ServicePackageListPage() {
  const { packages, requestClone } = useServicePackages();
  const history = useHistory();

  const [search, setSearch] = useState('');
  const [selectedCurrencies, setSelectedCurrencies] = useState<Set<string>>(
    new Set(),
  );

  const availableCurrencies = useMemo(
    () => [...new Set(packages.map((p) => p.currency))].sort(),
    [packages],
  );

  const toggleCurrency = (c: string) =>
    setSelectedCurrencies((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c);
      else next.add(c);
      return next;
    });

  const filteredPackages = useMemo(() => {
    return packages.filter((p) => {
      if (selectedCurrencies.size > 0 && !selectedCurrencies.has(p.currency))
        return false;
      if (search.trim()) {
        const label = PACKAGE_TYPE_LABELS[p.package_type].toLowerCase();
        if (!label.includes(search.trim().toLowerCase())) return false;
      }
      return true;
    });
  }, [packages, search, selectedCurrencies]);

  return (
    <div className="tw:flex tw:flex-col tw:gap-6 tw:p-6">
      <div className="tw:flex tw:flex-col tw:gap-1">
        <h1 className="tw:text-2xl tw:font-semibold">
          {i18n.t('ServicePackage:servicePackages')}
        </h1>
        <p className="tw:text-sm tw:text-muted-foreground">
          {i18n.t('ServicePackage:managePricingPlans')}
        </p>
      </div>

      {/* Filters */}
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-4">
        <div className="tw:relative">
          <Search className="tw:absolute tw:left-2.5 tw:top-1/2 tw:-translate-y-1/2 tw:size-4 tw:text-muted-foreground tw:pointer-events-none" />
          <Input
            placeholder={i18n.t('ServicePackage:searchByName')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="tw:pl-8 tw:w-48"
          />
        </div>

        {/* Currency chip toggles */}
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
          {availableCurrencies.map((c) => (
            <button
              key={c}
              onClick={() => toggleCurrency(c)}
              className={cn(
                'tw:text-xs tw:font-medium tw:px-2.5 tw:py-1 tw:rounded-md tw:border tw:transition-colors',
                selectedCurrencies.has(c)
                  ? 'tw:bg-primary tw:text-primary-foreground tw:border-primary'
                  : 'tw:bg-background tw:text-foreground tw:border-border tw:hover:bg-muted',
              )}
            >
              {c}
            </button>
          ))}
          {selectedCurrencies.size > 0 && (
            <button
              onClick={() => setSelectedCurrencies(new Set())}
              className="tw:text-xs tw:text-muted-foreground tw:hover:text-foreground tw:underline tw:underline-offset-2"
            >
              {i18n.t('ServicePackage:clear')}
            </button>
          )}
        </div>
      </div>

      {filteredPackages.length > 0 ? (
        <div className="tw:flex tw:flex-wrap tw:gap-4">
          {filteredPackages.map((pkg) => (
            <ServicePackageCard
              key={pkg.id}
              pkg={pkg}
              onManage={() =>
                history.push(
                  Routes.servicePackage(pkg.currency, pkg.package_type),
                )
              }
              onClone={() => requestClone(pkg.id)}
            />
          ))}
        </div>
      ) : (
        <p className="tw:text-sm tw:text-muted-foreground">
          {i18n.t('ServicePackage:noPackagesMatch')}
        </p>
      )}
    </div>
  );
}
